import { useCallback, useEffect, useState } from 'react';

import { useField } from 'react-final-form';
import { stringify, parse } from 'yaml';

import { useDebounce } from '@cast/utils';

import { RffCodeEditor, RffCodeEditorProps } from './RffCodeEditor';

type Props = Omit<RffCodeEditorProps, 'highlight'> & {
  indentation?: number;
};

export const RffYamlEditor = ({ indentation = 4, ...props }: Props) => {
  const field = useField(props.name);
  const { active } = field.meta;
  const { onChange, value } = field.input;
  const [editableValue, setEditableValue] = useState<string>('');
  const debouncedEditableValue = useDebounce(editableValue, 1000);

  const format = useCallback(
    (value?: string) => {
      if (!value) {
        return '';
      }

      try {
        return stringify(parse(value ?? ''), null, indentation);
      } catch {
        return value ?? '';
        // This will often be called, because while editing, yaml could be invalid
      }
    },
    [indentation]
  );

  useEffect(() => {
    if (debouncedEditableValue) {
      const stringified = format(debouncedEditableValue);
      onChange(stringified);
    }
  }, [debouncedEditableValue, format, onChange]);

  return (
    <RffCodeEditor
      {...props}
      onFocus={() => setEditableValue(format(value))}
      onBlur={() => {
        if (!editableValue) {
          onChange('');
          return;
        }
        const stringified = format(editableValue);
        onChange(stringified);
        setEditableValue('');
      }}
      onChange={setEditableValue}
      value={active ? editableValue : undefined}
      fieldProps={{
        ...props.fieldProps,
        format: (value, name) => {
          if (props.fieldProps?.format) {
            return props.fieldProps.format(value, name);
          }
          return format(value) ?? value ?? '';
        },
      }}
      highlight="yaml"
    />
  );
};
